import { create } from 'zustand';

interface ShortlistSession {
  showAll: boolean;
  scrollY: number;
  ctaRevealed: boolean;
}

const DEFAULT_SESSION: ShortlistSession = {
  showAll: false,
  scrollY: 0,
  ctaRevealed: false,
};

interface ShortlistStore {
  sessions: Record<string, ShortlistSession>;
  actions: {
    setShowAll: (key: string, showAll: boolean) => void;
    setScrollY: (key: string, scrollY: number) => void;
    setCtaRevealed: (key: string, ctaRevealed: boolean) => void;
    saveScrollPosition: (key: string) => void;
  };
}

function upsertSession(
  sessions: Record<string, ShortlistSession>,
  key: string,
  patch: Partial<ShortlistSession>,
) {
  const current = sessions[key] ?? DEFAULT_SESSION;

  return {
    ...sessions,
    [key]: { ...current, ...patch },
  };
}

const useShortlistStore = create<ShortlistStore>((set, get) => ({
  sessions: {},
  actions: {
    setShowAll: (key, showAll) =>
      set((state) => ({
        sessions: upsertSession(state.sessions, key, { showAll }),
      })),
    setScrollY: (key, scrollY) =>
      set((state) => ({
        sessions: upsertSession(state.sessions, key, { scrollY }),
      })),
    setCtaRevealed: (key, ctaRevealed) =>
      set((state) => ({
        sessions: upsertSession(state.sessions, key, { ctaRevealed }),
      })),
    saveScrollPosition: (key) => {
      const scrollY = window.scrollY;
      const { setScrollY, setCtaRevealed } = get().actions;

      setScrollY(key, scrollY);

      if (scrollY > 0) {
        setCtaRevealed(key, true);
      }
    },
  },
}));

export const useShortlistShowAll = (key: string) =>
  useShortlistStore((state) => state.sessions[key]?.showAll ?? false);

export const useShortlistScrollY = (key: string) =>
  useShortlistStore((state) => state.sessions[key]?.scrollY ?? 0);

export const useShortlistCtaRevealed = (key: string) =>
  useShortlistStore((state) => state.sessions[key]?.ctaRevealed ?? false);

export const useShortlistActions = () =>
  useShortlistStore((state) => state.actions);
